import React, { useState, useCallback, memo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiMoreVertical,
  FiEdit,
  FiUserCheck,
  FiUserX,
  FiEye,
} from "react-icons/fi";
import { toast } from "react-toastify";
import axios from "axios";

const api = axios.create({ baseURL: "https://algoritm-api-edu.uz" });
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("accessToken");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const MenuItem = memo(({ icon: Icon, text, color, hoverBg, onClick }) => (
  <motion.button
    whileHover={{ backgroundColor: hoverBg, x: 6 }}
    whileTap={{ scale: 0.97 }}
    onClick={onClick}
    style={{
      display: "flex",
      alignItems: "center",
      gap: "10px",
      width: "100%",
      padding: "12px 18px",
      background: "white",
      border: "none",
      textAlign: "left",
      fontSize: "14.5px",
      fontWeight: "500",
      cursor: "pointer",
    }}
  >
    <Icon size={18} color={color} />
    <span>{text}</span>
  </motion.button>
));

const UserActionsDropdown = memo(({ user, onEdit, onView, refreshList }) => {
  const [open, setOpen] = useState(false);

  const toggle = useCallback((e) => {
    e.stopPropagation();
    setOpen((v) => !v);
  }, []);

  const close = useCallback(
    (e) => {
      if (open) {
        e.stopPropagation();
        setOpen(false);
      }
    },
    [open]
  );

  const handleEditClick = useCallback(() => {
    onEdit();
    setOpen(false);
  }, [onEdit]);

  const handleViewClick = useCallback(() => {
    if (onView) {
      onView();
    } else {
      toast.info("Ko'rish funksiyasi ulanmagan");
    }
    setOpen(false);
  }, [onView]);

  const handleActivate = useCallback(async () => {
    if (user.state === "Active") {
      toast.warn("Foydalanuvchi allaqachon faol!");
      setOpen(false);
      return;
    }
    if (!window.confirm("Foydalanuvchi faollashtirilsinmi?")) return;
    try {
      await api.put(`/api/admin/Users/Activate/${user.id}`);
      toast.success("Faollashtirildi");
      refreshList();
    } catch (err) {
      toast.error("Faollashtirishda xato");
    } finally {
      setOpen(false);
    }
  }, [user.id, user.state, refreshList]);

  const handleDeactivate = useCallback(async () => {
    if (user.state !== "Active") {
      toast.warn("Foydalanuvchi allaqachon faol emas!");
      setOpen(false);
      return;
    }
    if (!window.confirm("Foydalanuvchi faolsizlantirilsinmi?")) return;
    try {
      await api.put(`/api/admin/Users/DeActivate/${user.id}`);
      toast.success("Faolsizlantirildi");
      refreshList();
    } catch (err) {
      toast.error("Xato yuz berdi");
    } finally {
      setOpen(false);
    }
  }, [user.id, user.state, refreshList]);

  if (!user || !user.id) {
    return (
      <button
        disabled
        style={{ opacity: 0.4, cursor: "not-allowed", padding: "6px 8px" }}
      >
        <FiMoreVertical size={20} color="#ccc" />
      </button>
    );
  }

  const isActive = user.state === "Active";

  return (
    <div style={{ position: "relative" }}>
      <motion.button
        whileHover={{ scale: 1.12 }}
        whileTap={{ scale: 0.92 }}
        onClick={toggle}
        style={{
          background: "rgba(255,255,255,0.8)",
          border: "1px solid #e2e8f0",
          borderRadius: "8px",
          padding: "6px 8px",
          cursor: "pointer",
        }}
      >
        <FiMoreVertical size={20} color="#64748b" />
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, scale: 0.92, y: -8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.92, y: -8 }}
            transition={{
              duration: 0.18,
              type: "spring",
              stiffness: 500,
              damping: 30,
            }}
            style={{
              position: "absolute",
              top: "50%",
              right: "100%",
              transform: "translateY(-50%)",
              marginRight: "12px",
              background: "white",
              borderRadius: "12px",
              boxShadow: "0 12px 40px rgba(0,0,0,0.16)",
              zIndex: 9999,
              minWidth: "195px",
              border: "1px solid #e2e8f0",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <MenuItem
              icon={FiEye}
              text="Ko‘rish"
              color="#4f46e5"
              hoverBg="#eef2ff"
              onClick={handleViewClick}
            />

            {isActive ? (
              <MenuItem
                icon={FiUserX}
                text="Faolsizlantirish"
                color="#dc2626"
                hoverBg="#fee2e2"
                onClick={handleDeactivate}
              />
            ) : (
              <>
                <MenuItem
                  icon={FiUserCheck}
                  text="Faollashtirish"
                  color="#16a34a"
                  hoverBg="#f0fdf4"
                  onClick={handleActivate}
                />
                <MenuItem
                  icon={FiEdit}
                  text="Tahrirlash"
                  color="#2563eb"
                  hoverBg="#eff6ff"
                  onClick={handleEditClick}
                />
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {open && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 9998,
            cursor: "default",
          }}
          onClick={close}
        />
      )}
    </div>
  );
});

UserActionsDropdown.displayName = "UserActionsDropdown";

export default UserActionsDropdown;
